import { useEffect, useState } from 'react'
import { Box, Network, Server, Shield } from 'lucide-react'
import { apiGet } from '../lib/api'
import { useTenantId } from '../context/SessionContext'
import { Card, KpiCard, PageHeader, Skeleton, StatusBadge } from '../components/ui'
import { Link } from 'react-router-dom'

interface Connector {
  id: string
  name: string
  is_active: boolean
  last_seen?: string
}

export default function AdminDashboardPage() {
  const tenantId = useTenantId()
  const [pools, setPools] = useState(0)
  const [connectors, setConnectors] = useState<Connector[]>([])
  const [resources, setResources] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!tenantId) return
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const [p, c, r] = await Promise.all([
          apiGet<unknown[]>(`/api/v1/tenants/${tenantId}/connector-pools`).catch(() => []),
          apiGet<Connector[]>(`/api/v1/tenants/${tenantId}/connectors`).catch(() => []),
          apiGet<unknown[]>(`/api/v1/tenants/${tenantId}/resources`).catch(() => []),
        ])
        if (cancelled) return
        setPools(Array.isArray(p) ? p.length : 0)
        setConnectors(Array.isArray(c) ? c : [])
        setResources(Array.isArray(r) ? r.length : 0)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [tenantId])

  const healthy = connectors.filter((c) => c.is_active).length
  const offline = connectors.length - healthy

  return (
    <div>
      <PageHeader
        title="Admin overview"
        description="Tenant infrastructure at a glance"
      />

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard label="Connector pools" value={pools} icon={<Network className="h-4 w-4" />} />
          <KpiCard label="Connectors" value={connectors.length} hint={`${healthy} healthy`} status="success" icon={<Server className="h-4 w-4" />} />
          <KpiCard label="Offline" value={offline} status={offline > 0 ? 'warning' : 'default'} />
          <KpiCard label="Resources" value={resources} icon={<Box className="h-4 w-4" />} />
        </div>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Connector health</h2>
            <Link to="/admin/connectors" className="text-xs font-medium text-primary hover:underline">Manage</Link>
          </div>
          {connectors.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted">No connectors registered</p>
          ) : (
            <ul className="space-y-2">
              {connectors.slice(0, 8).map((c) => (
                <li key={c.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{c.name}</p>
                    <p className="truncate font-mono text-xs text-muted">{c.id}</p>
                  </div>
                  <StatusBadge status={c.is_active ? 'healthy' : 'offline'} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="mb-4 text-sm font-semibold text-foreground">Quick links</h2>
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              { to: '/admin/pools', label: 'Connector Pools', icon: <Network className="h-4 w-4" /> },
              { to: '/admin/resources', label: 'Resources', icon: <Box className="h-4 w-4" /> },
              { to: '/admin/policies', label: 'Policies', icon: <Shield className="h-4 w-4" /> },
              { to: '/admin/connectors', label: 'Connectors', icon: <Server className="h-4 w-4" /> },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center gap-2 rounded-lg border border-border px-3 py-3 text-sm font-medium text-foreground hover:bg-surface-muted"
              >
                <span className="text-muted">{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </div>
        </Card>
      </div>
    </div>
  )
}
